import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLtiAuth } from '@/hooks/useLtiAuth';
import './lti-launch.css';

/**
 * Public landing page for LTI 1.3 launches.
 *
 * The LMS redirects the student's browser here after the OIDC callback
 * completes on the backend.  The URL carries two query params:
 *   - `lti_code`    — one-time code that the backend stored in Redis
 *   - `redirect_to` — the original target_link_uri the LMS intended
 *
 * This component calls `useLtiAuth` which exchanges `lti_code` for real
 * JWT tokens and stores them in localStorage.  Once the exchange is done,
 * the student is navigated to the `redirect_to` destination (or `/` if
 * none was provided).
 *
 * This route MUST remain outside of `ProtectedRoute` so unauthenticated
 * students can reach it before tokens are available.
 */
export function LtiLaunchPage() {
  const navigate = useNavigate();
  const { isExchanging, error, done } = useLtiAuth();

  useEffect(() => {
    if (!done) return;

    // After a successful exchange, navigate to the redirect_to destination.
    // useLtiAuth already stripped `lti_code` from the URL via history.replaceState,
    // but `redirect_to` is still present. Read it before navigating.
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirect_to');

    navigate(redirectTo || '/', { replace: true });
  }, [done, navigate]);

  if (error !== null) {
    return (
      <div className="lti-launch">
        <p className="lti-launch__error">LTI authentication failed: {error}</p>
      </div>
    );
  }

  return (
    <div className="lti-launch">
      {isExchanging && <div className="lti-launch__spinner" />}
      <p className="lti-launch__text">Launching assessment...</p>
    </div>
  );
}
